// LoSummary — the Loan Officer's PDF-style view of a finalized doc order.
// Instead of the editable section stack, the LO gets one compact sheet: loan
// terms, closing details, the final fee table, their compensation, the lender-
// credit block (P&P capacity language — an APPROVED copy exception to the
// prototype's yield wording), and an acknowledgement pad. All numbers come from
// the shared core; every control is gated on viewer.canActAsLo (LO2/non-LO
// viewers see the sheet fully read-only). Ported from LoSummary.jsx.
import { Fragment, useEffect, useState } from 'react';
import { Btn, Icon } from '../atoms';
import { useDocOrderCtx } from '../context';
import { fmtDate, fmtMoney, fmtStamp } from '../format';
import {
  cashLedgerAfterCredit,
  lenderCreditExceedsCapacity,
  netLoPayout,
  reimburseLines as coreReimburseLines,
  DO_CONFIG,
  type DocOrderFee,
  type FeeSplit,
  type PaidBy
} from '../core';
import type { DocOrderCapacity, DocOrderLoProfile, DocOrderRow } from '@/services/docOrderService';
import type { buildLoanFacts, buildSnapshotExtras } from '../viewModel';

interface LoSummaryProps {
  order: DocOrderRow;
  fees: DocOrderFee[];
  loanFacts: ReturnType<typeof buildLoanFacts>;
  extras: ReturnType<typeof buildSnapshotExtras>;
  capacity: DocOrderCapacity | null;
  loProfile: DocOrderLoProfile | null;
  summaryLoading?: boolean;
  summaryError?: string | null;
  onRetrySummary?: () => Promise<boolean>;
  /** Opens the text-only acknowledgement modal in the Workspace. */
  onAcknowledge: () => void;
  /** 'preview' = the closer's inert Preview Doc Order modal (R5 A1.4): every
   *  command and input is suppressed even for elevated viewers — elevation
   *  makes canActAsLo true, so no-op handlers alone are not enough. */
  mode?: 'preview';
}

const PAID_BY_LABEL: Record<PaidBy, string> = { Borrower: 'Borrower', LO: 'LO', Lendwise: 'Lendwise' };

/** Who fronted the fee: LO/Lendwise pay upfront; a borrower-POC fee is prepaid. */
function prepaidBy(f: Pick<DocOrderFee, 'paidBy' | 'timing'>): PaidBy | null {
  if (f.paidBy === 'LO' || f.paidBy === 'Lendwise') {
    return f.paidBy;
  }
  return f.paidBy === 'Borrower' && f.timing === 'POC' ? 'Borrower' : null;
}

function Row({ label, value, num, full }: { label: string; value: React.ReactNode; num?: boolean; full?: boolean }) {
  return (
    <div className={`lo-row${full ? ' full' : ''}`}>
      <span className="lo-row-l">{label}</span>
      <span className={`lo-row-v${num ? ' num' : ''}`}>{value || '—'}</span>
    </div>
  );
}

export function LoSummary({
  order,
  fees,
  loanFacts,
  extras,
  capacity,
  loProfile,
  summaryLoading = false,
  summaryError = null,
  onRetrySummary,
  onAcknowledge,
  mode
}: LoSummaryProps) {
  const {
    viewer,
    now,
    setLenderCredit,
    escalateLenderCredit,
    withdrawLenderCreditEscalation,
    loRequestChanges,
    busy
  } = useDocOrderCtx();
  const isPreview = mode === 'preview';
  const canAct = !isPreview && viewer.canActAsLo;
  const stage = order.stage;

  const effectiveFp = order.first_payment_override?.month ?? order.first_payment_date;
  const fpLabel = effectiveFp
    ? new Date(`${effectiveFp}-02T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '—';
  const isFhaVa = /\b(fha|va)\b/i.test(loanFacts.product || '');
  const chargedFees = fees.filter((f) => Number(f.amount) > 0 && !f.notApplicable);
  const dispRank = (f: DocOrderFee) => {
    const p = prepaidBy(f);
    return p === 'LO' ? 0 : p === 'Lendwise' ? 2 : 1;
  };
  const sortedFees = [...chargedFees].sort((a, b) => dispRank(a) - dispRank(b));
  const totalFees = chargedFees.reduce((a, f) => a + (Number(f.amount) || 0), 0);
  const splitsOf = (f: DocOrderFee): FeeSplit[] | null => (f.splits && f.splits.length >= 2 ? f.splits : null);
  const paidByLabel = (v: PaidBy) => PAID_BY_LABEL[v] ?? v;

  // ---- compensation (recon-initialization parity via core netLoPayout) ----
  const reimburseLineList = coreReimburseLines(fees);
  const baseComp = loProfile?.comp ?? 0;
  const netComp = netLoPayout(baseComp, fees);

  // ---- lender credit (P&P capacity model — copy exception) ----
  const lenderCredit = Number(order.lender_credit) || 0;
  const floor = capacity?.floor ?? 0;
  const loanPrice = capacity?.loanPrice ?? null;
  const maxAllowed = capacity?.maxAllowed4794 ?? 0;
  const headroom = capacity?.headroom ?? 0;
  const cash142 = Number(loanFacts.cashToFromBorrower) || 0;
  const newBalance = cashLedgerAfterCredit(cash142, lenderCredit);
  const escalation = order.lender_credit_escalation;

  const [lcDraft, setLcDraft] = useState(lenderCredit ? String(lenderCredit) : floor ? String(floor) : '');
  const [serverOverCapacity, setServerOverCapacity] = useState(false);
  const [lcError, setLcError] = useState<string | null>(null);
  const lcDraftNum = Number(lcDraft) || 0;
  const canEditLc = canAct && stage === 'lo_review';
  const belowFloor = floor > 0 && lcDraftNum < floor - 0.005;
  const exceedsCapacity = capacity ? lenderCreditExceedsCapacity(lcDraftNum, maxAllowed) : lcDraftNum > 0;
  const showEscalate = exceedsCapacity || serverOverCapacity;
  const hasDraft = lcDraft.trim() !== '' && Number.isFinite(Number(lcDraft));
  const canApply =
    !!capacity &&
    hasDraft &&
    lcDraftNum >= 0 &&
    !belowFloor &&
    !showEscalate &&
    Math.abs(lcDraftNum - lenderCredit) >= 0.005;

  useEffect(() => {
    setLcDraft(lenderCredit ? String(lenderCredit) : floor ? String(floor) : '0');
  }, [floor, lenderCredit]);

  const applyLc = async () => {
    setLcError(null);
    if (!hasDraft || lcDraftNum < 0 || belowFloor || !capacity) {
      return;
    }
    const res = await setLenderCredit(lcDraftNum);
    if (res.ok) {
      setServerOverCapacity(false);
    } else if (res.code === 'over_capacity') {
      setServerOverCapacity(true);
    } else if (!res.ok && res.error) {
      setLcError(res.error);
    }
  };
  const escalateLc = async () => {
    setLcError(null);
    if (!(lcDraftNum > 0)) {
      return;
    }
    await escalateLenderCredit(lcDraftNum);
  };
  const resetLc = async () => {
    const amount = floor > 0 ? floor : 0;
    setLcDraft(String(amount));
    setLcError(null);
    const res = await setLenderCredit(amount);
    if (!res.ok && res.error) {
      setLcError(res.error);
    }
  };

  // ---- request-changes round-trip ----
  const [reqOpen, setReqOpen] = useState(false);
  const [reqNote, setReqNote] = useState('');
  const [reqRows, setReqRows] = useState<Record<string, { amount: string; reimburse: boolean }>>({});
  const beginRequest = () => {
    const init: Record<string, { amount: string; reimburse: boolean }> = {};
    chargedFees.forEach((f) => {
      init[f.id] = { amount: String(f.amount), reimburse: !!f.reimburse };
    });
    setReqRows(init);
    setReqNote('');
    setReqOpen(true);
  };
  const submitRequest = async () => {
    const note = reqNote.trim();
    if (!note) {
      return;
    }
    // Changed-only payload: only fees whose amount or reimburse moved.
    const items = chargedFees
      .map((f) => {
        const r = reqRows[f.id] ?? { amount: String(f.amount), reimburse: !!f.reimburse };
        const toAmount = Number(r.amount);
        const amtChanged = Number.isFinite(toAmount) && Math.abs(toAmount - Number(f.amount)) > 0.005;
        const toReimburse = !!r.reimburse;
        const reimbChanged = toReimburse !== !!f.reimburse;
        if (!amtChanged && !reimbChanged) {
          return null;
        }
        return { id: f.id, toAmount: amtChanged ? toAmount : Number(f.amount), toReimburse };
      })
      .filter((x): x is { id: string; toAmount: number; toReimburse: boolean } => x !== null);
    const res = await loRequestChanges(note, items);
    if (res.ok) {
      setReqOpen(false);
    }
  };

  // ---- acknowledgement pad ----
  const writeStatus = order.lender_credit_write_status ?? null;
  const ackPending = !!order.acknowledged_at && (writeStatus === 'pending' || writeStatus === 'waiting_unlock' || writeStatus === 'waiting_retry');
  const ackFailed = writeStatus === 'failed';
  const acknowledged = stage === 'complete';
  const economicsReady = !summaryLoading && !summaryError && capacity !== null && loProfile !== null;
  const canSign = canAct && stage === 'lo_review' && !ackPending && economicsReady;
  const economicsPending = canAct && stage === 'lo_review' && !ackPending && !economicsReady;
  const loName = extras.loanOfficer ?? 'Loan Officer';

  const flags = [
    order.vesting_workflow?.closingInTrust && 'Closing in Trust',
    order.vesting_workflow?.closingInLlc && 'Closing in LLC',
    order.power_of_attorney === 'Yes' && 'Power of Attorney',
    order.vesting_workflow?.titleOnlyBorrower && `Title Only · ${order.vesting_workflow?.titleOnlyName || '—'}`
  ].filter(Boolean) as string[];

  return (
    <div className="lo-doc-wrap">
      <article className="lo-doc">
        {!isPreview && summaryError && (
          <div className="banner warn" style={{ marginBottom: 12 }} role="alert">
            <Icon name="alertTriangle" size={14} className="banner-ico" />
            <span style={{ flex: 1 }}>{summaryError}</span>
            {onRetrySummary && (
              <button type="button" className="recon-btn attn" disabled={summaryLoading} onClick={() => void onRetrySummary()}>
                Retry
              </button>
            )}
          </div>
        )}
        <header className="lo-doc-head">
          <div className="lo-doc-brand">
            <div>
              <div className="lo-doc-kicker">Lendwise Mortgage · Doc Order Summary</div>
              <h1 className="lo-doc-title">{loanFacts.borrowers.join(' & ') || '—'}</h1>
            </div>
          </div>
          <div className="lo-doc-meta">
            <div>
              <span>Loan #</span>
              <b className="num">{loanFacts.loanNumber}</b>
            </div>
            <div>
              <span>Closing</span>
              <b className="num">{fmtDate(order.closing_date)}</b>
            </div>
          </div>
        </header>

        <div className="lo-doc-sub">
          <span>
            <Icon name="mapPin" size={12} /> {loanFacts.property || '—'}
          </span>
          <span>
            <Icon name="user" size={12} /> LO {loName}
          </span>
          {extras.processor && (
            <span>
              <Icon name="clipboard" size={12} /> Processor {extras.processor}
            </span>
          )}
        </div>

        {/* Loan terms */}
        <section className="lo-block">
          <h2 className="lo-block-h">Loan Terms</h2>
          <div className="lo-grid">
            {isFhaVa ? (
              <Row
                label="Base / Total Loan"
                value={`${fmtMoney(loanFacts.baseLoanAmount !== null ? loanFacts.baseLoanAmount : loanFacts.loanAmount)} / ${fmtMoney(loanFacts.loanAmount)}`}
                num
              />
            ) : (
              <Row label="Loan Amount" value={fmtMoney(loanFacts.loanAmount)} num />
            )}
            <Row label="Note Rate" value={extras.rate !== null ? `${extras.rate}%` : '—'} num />
            <Row label="Product" value={loanFacts.product} />
            <Row label="Purpose" value={loanFacts.purpose} />
            <Row label="LTV / CLTV" value={extras.ltv !== null || extras.cltv !== null ? `${extras.ltv ?? '—'}% / ${extras.cltv ?? '—'}%` : '—'} num />
            <Row
              label="DTI (Housing / Total)"
              value={extras.housingRatio !== null || extras.dtiRatio !== null ? `${extras.housingRatio ?? '—'}% / ${extras.dtiRatio ?? '—'}%` : '—'}
              num
            />
            <Row label="Occupancy" value={loanFacts.occupancy} />
            <Row label="Property Type" value={loanFacts.propertyType} />
            <Row label="Lock Expiration" value={fmtDate(loanFacts.lockExpiration)} num />
          </div>
        </section>

        {/* Closing details */}
        <section className="lo-block">
          <h2 className="lo-block-h">Closing Details</h2>
          <div className="lo-grid">
            <Row label="Borrower Vesting" value={order.encompass_vesting || '—'} full />
            <Row label="Signing Date" value={fmtDate(order.signing_date)} num />
            <Row label="Funding Date" value={fmtDate(order.closing_date)} num />
            <Row label="First Payment" value={fpLabel} />
            <Row label="Signing Method" value={order.sign_type} />
          </div>
          {flags.length > 0 && (
            <div className="lo-flags">
              {flags.map((f) => (
                <span key={f} className="lo-flag">
                  <Icon name="check" size={11} />
                  {f}
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Final fees */}
        <section className="lo-block">
          <h2 className="lo-block-h">Final Third-Party Fees</h2>
          <table className="lo-fees">
            <thead>
              <tr>
                <th>Fee</th>
                <th>Paid By</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {sortedFees.map((f) => {
                const sp = splitsOf(f);
                if (!sp) {
                  const p = prepaidBy(f);
                  const label = p ? `POC ${paidByLabel(p)}` : '';
                  const loReimb = f.reimburse && f.paidBy === 'LO';
                  return (
                    <tr key={f.id} className={loReimb ? 'reimb' : ''}>
                      <td>{f.name}</td>
                      <td className="lo-disp">
                        <span className="lo-disp-code">{label}</span>
                        {loReimb && (
                          <span className="lo-reimb-chip" title="Reimbursed to the loan officer at closing">
                            <Icon name="rotateCcw" size={9} />
                            Reimbursed
                          </span>
                        )}
                      </td>
                      <td className="num">{fmtMoney(f.amount, true)}</td>
                    </tr>
                  );
                }
                const feeTotal = Number(f.amount) || 0;
                return (
                  <Fragment key={f.id}>
                    <tr className="lo-fee-splithead">
                      <td>
                        {f.name} <span className="lo-split-tag">Split</span>
                      </td>
                      <td className="lo-disp" />
                      <td className="num">{fmtMoney(feeTotal, true)}</td>
                    </tr>
                    {sp.map((s, i) => {
                      const p = prepaidBy(s);
                      const label = p ? `POC ${paidByLabel(p)}` : s.paidBy ? paidByLabel(s.paidBy) : '—';
                      const loReimb = s.reimburse && s.paidBy === 'LO';
                      const pct = feeTotal ? Math.round(((Number(s.amount) || 0) / feeTotal) * 1000) / 10 : 0;
                      return (
                        <tr key={`${f.id}-${i}`} className={`lo-fee-portion ${loReimb ? 'reimb' : ''}`}>
                          <td className="lo-portion-name">
                            {/* Split-model revision (Jennifer bugs 10/11): every
                                portion is a numbered split, 1-based — same labels
                                as the fee table and the recon feed. */}
                            <span className="lo-portion-ix">{`Split ${i + 1}`}</span>
                            <span className="lo-portion-pct">{pct}%</span>
                          </td>
                          <td className="lo-disp">
                            <span className="lo-disp-code">{label}</span>
                            {loReimb && (
                              <span className="lo-reimb-chip" title="Reimbursed to the loan officer at closing">
                                <Icon name="rotateCcw" size={9} />
                                Reimbursed
                              </span>
                            )}
                          </td>
                          <td className="num">{fmtMoney(s.amount, true)}</td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="total">
                <td colSpan={2}>Total third-party fees</td>
                <td className="num">{fmtMoney(totalFees, true)}</td>
              </tr>
            </tfoot>
          </table>
        </section>

        {/* Lender credit — P&P capacity language (approved copy exception) */}
        <section className="lo-block lc-block">
          <h2 className="lo-block-h">Lender Credit to Borrower</h2>
          <div className="lc-print-line">
            {lenderCredit > 0 ? `Lender credit of ${fmtMoney(lenderCredit, true)} applied to the borrower.` : 'No lender credit applied to the borrower.'}
          </div>
          <div className="lc-bar">
            <div className="lc-facts">
              <div className="lc-fact">
                <span className="lc-fk">Loan Price</span>
                <span className="lc-fv num">{loanPrice !== null ? loanPrice.toFixed(3) : summaryLoading ? 'Loading…' : 'Unavailable'}</span>
              </div>
              <div className="lc-fact">
                <span className="lc-fk">Available Lender Credit</span>
                <span className="lc-fv accent">{capacity ? fmtMoney(headroom, true) : summaryLoading ? 'Loading…' : 'Unavailable'}</span>
              </div>
            </div>
            <div className="lc-ledger">
              <div className="lc-led-row">
                <span className="lc-led-l">Cash {cash142 >= 0 ? 'from' : 'to'} borrower</span>
                <span className="lc-led-v">{fmtMoney(Math.abs(cash142), true)}</span>
              </div>
              <div className="lc-led-row">
                <span className="lc-led-l">Lender credit</span>
                <span className="lc-led-v neg">{lenderCredit > 0 ? `− ${fmtMoney(lenderCredit, true)}` : '—'}</span>
              </div>
              <div className="lc-led-row lc-led-total">
                <span className="lc-led-l">{newBalance >= 0 ? 'Cash from borrower' : 'Cash to borrower'}</span>
                <span className="lc-led-v">{fmtMoney(Math.abs(newBalance), true)}</span>
              </div>
            </div>
          </div>
          {canEditLc && (
            <div className="lc-apply-inline">
              <div className={`lo-lc-field ${belowFloor || showEscalate ? 'err' : ''}`}>
                <span className="lo-lc-prefix">$</span>
                <input
                  className="lo-lc-input"
                  type="number"
                  min={floor}
                  step="1"
                  value={lcDraft}
                  placeholder="0"
                  disabled={!capacity || summaryLoading || busy}
                  onChange={(e) => {
                    setLcDraft(e.target.value);
                    setServerOverCapacity(false);
                    setLcError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canApply) {
                      e.preventDefault();
                      void applyLc();
                    }
                  }}
                />
              </div>
              {showEscalate ? (
                <button
                  type="button"
                  className="lo-lc-escalate"
                  disabled={!!escalation || !(lcDraftNum > 0) || busy}
                  onClick={() => void escalateLc()}
                >
                  <Icon name="send" size={11} />
                  Escalate to Lock Desk
                </button>
              ) : (
                <button type="button" className="lo-lc-send" disabled={!canApply || busy} onClick={() => void applyLc()}>
                  <Icon name="check" size={11} />
                  {lenderCredit > 0 ? 'Update' : 'Apply credit'}
                </button>
              )}
            </div>
          )}
          {canEditLc && belowFloor && (
            <span className="lo-lc-err" style={{ marginTop: 6 }}>
              <Icon name="alertTriangle" size={11} />
              {fmtMoney(floor, true)} was disclosed on the last LE — you can increase but not reduce it.
            </span>
          )}
          {canEditLc && showEscalate && !belowFloor && (
            <span className="lo-lc-err" style={{ marginTop: 6 }}>
              <Icon name="alertTriangle" size={11} />
              Exceeds Available Lender Credit — escalate to the lock desk to request more.
            </span>
          )}
          {canEditLc && lcError && (
            <span className="lo-lc-err" style={{ marginTop: 6 }}>
              <Icon name="alertTriangle" size={11} />
              {lcError}
            </span>
          )}
          {canEditLc && floor > 0 && !belowFloor && !showEscalate && (
            <span className="lo-lc-note2">
              <Icon name="lock" size={10} />
              {fmtMoney(floor, true)} disclosed on the last LE · increase-only
            </span>
          )}
          {canEditLc && lenderCredit > 0 && !showEscalate && (
            <button type="button" className="lo-lc-reset" disabled={busy || !capacity} onClick={() => void resetLc()}>
              {floor > 0 ? 'Reset to disclosed' : 'Remove'}
            </button>
          )}
          {escalation && (
            <span className="lo-lc-escalated" style={{ marginTop: 6 }}>
              <Icon name="clock" size={11} />
              Escalated to the lock desk · {fmtMoney(escalation.amount, true)} pending
              {escalation.notify ? ` · emailed ${escalation.notify.to}` : ''}
              {canEditLc && (
                <button
                  type="button"
                  className="lo-lc-withdraw"
                  disabled={busy}
                  onClick={() => void withdrawLenderCreditEscalation()}
                >
                  Withdraw
                </button>
              )}
            </span>
          )}
        </section>

        {/* Compensation */}
        <section className="lo-block lo-comp-block">
          <h2 className="lo-block-h">Your Compensation</h2>
          {!loProfile ? (
            <p className="lo-comp-unavailable">
              {summaryLoading ? 'Loading compensation details…' : 'Compensation details are unavailable for this order.'}
            </p>
          ) : (
          <div className="lo-comp-grid">
            <div className="lo-comp-hero">
              <div className="lo-comp-label">Net payout at closing</div>
              <div className="lo-comp-big">{fmtMoney(netComp)}</div>
            </div>
            <div className="lo-comp-lines">
              <Row
                label={`Base comp${loProfile?.bps !== null && loProfile?.bps !== undefined ? ` · ${loProfile.bps} bps · ${loProfile.compType}` : ''}`}
                value={fmtMoney(baseComp)}
                num
              />
              {reimburseLineList.map((r) => (
                <Row key={r.key} label={`${r.name} Reimbursement`} value={fmtMoney(r.amount, true)} num />
              ))}
              <Row label="E&O Insurance" value={`− ${fmtMoney(DO_CONFIG.eoInsurance, true)}`} num />
              <div className="lo-row total">
                <span className="lo-row-l">Net payout</span>
                <span className="lo-row-v num strong">{fmtMoney(netComp)}</span>
              </div>
            </div>
          </div>
          )}
        </section>

        {/* Acknowledgement */}
        <section className="lo-sign">
          <div className="lo-sign-text">
            <h2 className="lo-block-h">Acknowledgement</h2>
            <p>
              By signing, <b>{loName}</b> confirms review of the final third-party fees and compensation shown above.
            </p>
            {canSign && (
              <button type="button" className="btn btn-ghost" style={{ marginTop: 10 }} disabled={busy} onClick={beginRequest}>
                <Icon name="rotateCcw" size={13} />
                Request changes from Closing
              </button>
            )}
            {order.lo_change_request && stage === 'submitted' && (
              <p style={{ color: 'var(--warn-fg)', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <Icon name="rotateCcw" size={12} />
                Sent back to Closing with requested changes on {fmtStamp(order.lo_change_request.at)}.
              </p>
            )}
          </div>
          <div
            className={`lo-sign-pad ${acknowledged ? 'signed' : canSign ? 'clickable' : ''}`}
            onClick={() => {
              if (canSign && !busy) {
                onAcknowledge();
              }
            }}
            title={canSign ? 'Click to acknowledge & sign' : undefined}
          >
            {acknowledged ? (
              <div>
                <div className="lo-sign-script">{loName}</div>
                <div className="lo-sign-date">Acknowledged {fmtDate(order.acknowledged_at ?? now.toISOString())}</div>
              </div>
            ) : ackPending ? (
              <div className="lo-sign-wait">
                <Icon name="clock" size={18} />
                <span>Acknowledged — Encompass write pending</span>
              </div>
            ) : canSign ? (
              <div className="lo-sign-cta">
                <Icon name="penTool" size={20} />
                <span>Acknowledge &amp; sign</span>
              </div>
            ) : economicsPending ? (
              <div className="lo-sign-wait">
                <Icon name={summaryLoading ? 'clock' : 'alertTriangle'} size={18} />
                <span>{summaryLoading ? 'Loading compensation & lender credit' : 'Compensation & lender credit unavailable'}</span>
              </div>
            ) : (
              <div className="lo-sign-wait">
                <Icon name="clock" size={18} />
                <span>Awaiting Closing confirmation</span>
              </div>
            )}
          </div>
        </section>

        {ackPending && (
          <div className="banner gold" style={{ marginTop: 14 }}>
            <Icon name="clock" size={16} className="banner-ico" />
            <span>
              <b>Acknowledged — Encompass lender-credit write pending.</b> The order completes automatically once the write validates; reopen to check status.
            </span>
          </div>
        )}
        {ackFailed && (
          <div className="banner warn" style={{ marginTop: 14 }}>
            <Icon name="alertTriangle" size={16} className="banner-ico" />
            <span style={{ flex: 1 }}>The lender-credit write to Encompass failed.</span>
            {canAct && (
              <button type="button" className="recon-btn attn" disabled={busy} onClick={onAcknowledge}>
                Retry acknowledgement
              </button>
            )}
          </div>
        )}
      </article>

      {reqOpen && (
        <div className="modal-backdrop" onClick={() => setReqOpen(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">
                <span style={{ display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: 'var(--r-md)', background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                  <Icon name="rotateCcw" size={18} />
                </span>
                Request changes from Closing
              </div>
            </div>
            <div className="modal-body">
              <p style={{ margin: '4px 0 14px', color: 'var(--fg-muted)', lineHeight: 1.6 }}>
                Propose updated fee amounts or reimbursement and explain what needs to change. This sends the order back to <b>Closing</b> for revision.
              </p>
              <table className="lo-fees" style={{ marginBottom: 16 }}>
                <thead>
                  <tr>
                    <th>Fee</th>
                    <th className="num">Current</th>
                    <th className="num">Requested</th>
                    <th>Reimburse LO</th>
                  </tr>
                </thead>
                <tbody>
                  {chargedFees.map((f) => {
                    const r = reqRows[f.id] ?? { amount: String(f.amount), reimburse: !!f.reimburse };
                    return (
                      <tr key={f.id}>
                        <td>{f.name}</td>
                        <td className="num">{fmtMoney(f.amount, true)}</td>
                        <td className="num">
                          <span className="lo-lc-field" style={{ display: 'inline-flex', width: 110 }}>
                            <span className="lo-lc-prefix">$</span>
                            <input
                              className="lo-lc-input"
                              type="number"
                              min="0"
                              step="1"
                              value={r.amount}
                              onChange={(e) => setReqRows({ ...reqRows, [f.id]: { ...r, amount: e.target.value } })}
                            />
                          </span>
                        </td>
                        <td>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={r.reimburse}
                              style={{ width: 15, height: 15, accentColor: 'var(--accent)' }}
                              onChange={(e) => setReqRows({ ...reqRows, [f.id]: { ...r, reimburse: e.target.checked } })}
                            />
                            {r.reimburse ? 'Yes' : 'No'}
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <label className="field-label" style={{ display: 'block', marginBottom: 6 }}>
                What needs to change? <span className="req">*</span>
              </label>
              <textarea
                className="recon-note"
                rows={3}
                value={reqNote}
                placeholder="e.g. Appraisal fee should be $895 per the revised invoice; reimburse the LO for the credit report."
                onChange={(e) => setReqNote(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div className="modal-foot">
              <Btn variant="ghost" onClick={() => setReqOpen(false)}>
                Cancel
              </Btn>
              <Btn variant="primary" icon="send" disabled={!reqNote.trim() || busy} onClick={() => void submitRequest()}>
                Send back to Closing
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
